import PackageType from 'src/constants/packageType';
import { isDateStringOlderThanNYears } from 'src/utils/helpers.js';

export const getDocumentsBasic = (state, _, rootState) => {
  return getDocumentsByCardGroup(state, rootState.contract.contracts.basicCardGroup);
};

export const getDocumentsAdditional = (state, _, rootState) => {
  return getDocumentsByCardGroup(state, rootState.contract.contracts.additionalCardGroup);
};

export const getDocumentsDental = (state, _, rootState) => {
  return getDocumentsByCardGroup(state, rootState.contract.contracts.dentalCardGroup);
};
export const getDocumentsExtra = (state, _, rootState) => {
  return getDocumentsByCardGroup(state, rootState.contract.contracts.extraAdditionalCardGroup);
};

export const getSpecificDocument = (state, _, rootState) => (productComponentId) => {
  return [
    getDocumentInfoByCard(
      state,
      rootState.contract.contracts.allCards.find((x) => x.productComponentCode === productComponentId),
    ),
  ];
};

export const getParticipantsBasic = (state, _, rootState) => {
  return getParticipantsByProductType(state, rootState, rootState.contract.contracts.basicCardGroup).filter(
    (x) => !state.newlyParticipantCustomerNumbers.includes(x.customer.customerNumber),
  );
};

export const getNewlyParticipantsBasic = (state, _, rootState) => {
  return getParticipantsByProductType(state, rootState, rootState.contract.contracts.basicCardGroup).filter((x) =>
    state.newlyParticipantCustomerNumbers.includes(x.customer.customerNumber),
  );
};

export const getParticipantsAdditional = (state, _, rootState) => {
  return getParticipantsByProductType(state, rootState, rootState.contract.contracts.additionalCardGroup).filter(
    (x) => !state.newlyParticipantCustomerNumbers.includes(x.customer.customerNumber),
  );
};

export const getNewlyParticipantsAdditional = (state, _, rootState) => {
  return getParticipantsByProductType(state, rootState, rootState.contract.contracts.additionalCardGroup).filter((x) =>
    state.newlyParticipantCustomerNumbers.includes(x.customer.customerNumber),
  );
};

export const getParticipantsEmployers = (state, _, rootState) => {
  return getParticipantsByProductType(state, rootState, rootState.contract.contracts.extraAdditionalCardGroup).filter(
    (x) => !state.newlyParticipantCustomerNumbers.includes(x.customer.customerNumber),
  );
};

export const getNewlyParticipantsEmployers = (state, _, rootState) => {
  return getParticipantsByProductType(state, rootState, rootState.contract.contracts.extraAdditionalCardGroup).filter(
    (x) => state.newlyParticipantCustomerNumbers.includes(x.customer.customerNumber),
  );
};

export const getParticipantsDentist = (state, _, rootState) => {
  return getParticipantsByProductType(state, rootState, rootState.contract.contracts.dentalCardGroup, false).filter(
    (x) => !state.newlyParticipantCustomerNumbers.includes(x.customer.customerNumber),
  );
};

export const getNewlyParticipantsDentist = (state, _, rootState) => {
  return getParticipantsByProductType(state, rootState, rootState.contract.contracts.dentalCardGroup, true).filter(
    (x) => state.newlyParticipantCustomerNumbers.includes(x.customer.customerNumber),
  );
};

export const getShowMedicalSelectionOverlay = (state) => {
  return state.showMedicalSelectionOverlay;
};

export const getShowDenialReason = (state) => {
  return state.showDenialReason;
};

export const getMedicalSelectionOverlayData = (state) => {
  return state.medicalSelectionOverlayData;
};

export const getAgreementId = (state) => {
  return state.agreementId;
};

export const getShowRejected = (state) => {
  return state.showRejected;
};

export const getDenialReasonData = (state) => {
  return state.denialReasonData;
};

export const getRejectedData = (state) => {
  return state.rejectedData;
};

function getDocumentsByCardGroup(state, cardGroup) {
  const docs = [];
  const cards = cardGroup.cards.filter(
    (x) => !state.productComponentCodesToIgnore.find((y) => y === x.productComponentCode),
  );
  cards.forEach((card) => {
    if (card.productComponentCode && !docs.map((doc) => doc.code).includes(Number(card.productComponentCode))) {
      docs.push(getDocumentInfoByCard(state, card));
    }
  });

  return docs;
}

function getDocumentInfoByCard(state, card) {
  return {
    code: Number(card.productComponentCode),
    contractCode: state.contractCode,
    year: state.year,
    title: card.title,
    insuranceCardLink: card.insuranceCardLink,
  };
}

function getParticipantsByProductType(state, rootState, cardGroup, ignoreUnder18 = false) {
  let cards = cardGroup.cards.filter(
    (x) => !state.productComponentCodesToIgnore.find((y) => y === x.productComponentCode),
  );

  cards = removeCardWithNoCostAndOnlyCardInSetNumberList(cards);

  const participants = [];

  rootState.contract.contracts.customers.forEach((x) => {
    if (ignoreUnder18 && !isDateStringOlderThanNYears(x.birthDate, 18)) {
      return;
    }
    const card = getCardByCustomer(cards, x.customerNumber);

    let ownRiskOptions = [];
    if (card?.packageTypeForWebApplications?.productType === PackageType.BasicInsurance) {
      ownRiskOptions = getInsuranceOptions(rootState.contract.contracts.excessCardGroup.cards, x);
    }
    const participant = {
      customer: {
        fullName: x.fullName,
        birthDate: x.birthDate,
        customerNumber: x.customerNumber,
        role: x.role.Role,
      },
      options: getInsuranceOptions(cards, x),
      ownRiskOptions: ownRiskOptions,
    };

    participants.push(participant);
  });

  return participants;
}

function removeCardWithNoCostAndOnlyCardInSetNumberList(cards) {
  const pakketsetCount = cards.reduce((countMap, card) => {
    countMap[card.packageSetNumber] = (countMap[card.packageSetNumber] || 0) + 1;
    return countMap;
  }, {});

  return cards.filter((card) => {
    return (
      card.insuredPersons.some((y) => y.netPremiumWithDiscount != null) || pakketsetCount[card.packageSetNumber] > 1
    );
  });
}

function getCardByCustomer(cards, customerNumber) {
  return cards.find((card) =>
    card.insuredPersons.some((person) => Number(person.customerNumber) === customerNumber && person.isSelected),
  );
}

function getInsuranceOptions(cards, customer) {
  const insuranceOptions = [];

  cards.forEach((insuranceCard) => {
    const person = findPersonByCard(insuranceCard, customer.customerNumber);
    const option = {
      name: customer.customerNumber,
      key: insuranceCard.productComponentCode ?? 0,
      value: getProductNameByCard(insuranceCard),

      subtitle: `edit-policy.insurances.subtitle.${insuranceCard.productComponentCode}`,
      selected: person?.isSelected,
      amount: person?.netPremiumWithDiscount,
      ownRiskDiscount: person?.ownRiskDiscount,
      disabled: optionDisabled(person, insuranceCard.packageTypeForWebApplications.productType) !== undefined,
      disabledText: optionDisabled(person, insuranceCard.packageTypeForWebApplications.productType),
      showWarningIcon: insuranceCard.hasMedicalSelection,
      productType: insuranceCard.packageTypeForWebApplications.productType,
      showFreePresent: showFreePresent(insuranceCard.packageTypeForWebApplications.productType, insuranceCard),
    };

    if (option.amount !== undefined) {
      insuranceOptions.push(option);
    }
  });
  return insuranceOptions;
}

function showFreePresent(productType, insuranceCard) {
  return (
    productType === PackageType.ExtraAdditionalInsurance &&
    insuranceCard.insuredPersons.every((y) => y.netPremiumWithDiscount == 0)
  );
}

function findPersonByCard(card, customerNumber) {
  if (!card) return undefined;
  return card.insuredPersons.find((person) => Number(person.customerNumber) === customerNumber);
}

function getProductNameByCard(card) {
  return card.packageTypeForWebApplications?.productType === PackageType.BasicInsurance ||
    card.packageTypeForWebApplications?.productType === PackageType.OwnRisk
    ? `edit-policy.insurances.title.${card.productComponentCode}`
    : card.title
      ? card.title
      : 'edit-policy.labels.none';
}

function optionDisabled(person, productType) {
  if (productType === PackageType.BasicInsurance && person?.isDisabled) return 'edit-policy.tooltip.basic-disabled';
  if (person?.isDisabled) return 'edit-policy.tooltip.disabled';
  if (
    person?.isSelected &&
    person?.netPremiumWithDiscount === 0 &&
    productType === PackageType.ExtraAdditionalInsurance
  )
    return 'edit-policy.tooltip.free-disabled';
  return undefined;
}
